import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MessageDialogService } from '../../services/message-dialog';
import { DeliveryReportService, LoadReportResponse, LoadReportRow, LoadReportLoad } from '../../services/delivery-report.servic';
import { LoadManagementService, FilteredLoad } from '../../services/load-management.service';

@Component({
  selector: 'app-load-delivery-report',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatButtonModule,
    MatCardModule,
    MatTableModule,
    MatSelectModule,
    MatInputModule,
    MatIconModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './load-delivery-report-component.html',
  styleUrls: ['./load-delivery-report-component.scss']
})
export class LoadDeliveryReportComponent implements OnInit {
  startDate: string = '';
  endDate: string = '';
  page: number = 1;
  pageSize: number = 10;
  totalPages: number = 1;
  loads: FilteredLoad[] = [];
  selectedLoad: FilteredLoad | null = null;
  loadReportData: LoadReportResponse | null = null;
  isLoading = false;
  isReportLoading = false;

  // Table columns for the report
  displayedColumns: string[] = [
    'slno',
    'customerName',
    'orderNumber',
    'amount',
    'invoiceNumber',
    'invoiceAmount',
    'oldUnpaidInvoiceNumbers',
    'oldUnpaidTotalAmount',
    'total',
    'notes'
  ];

  constructor(
    private loadService: LoadManagementService,
    private deliveryReportService: DeliveryReportService,
    private messageDialogService: MessageDialogService
  ) {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    this.startDate = today.toISOString().split('T')[0];
    this.endDate = tomorrow.toISOString().split('T')[0];
  }

  ngOnInit(): void {
    this.fetchLoads();
  }

  fetchLoads(): void {
    if (!this.startDate || !this.endDate) {
      this.messageDialogService.warning('Please select start and end dates.');
      return;
    }

    this.isLoading = true;
    const payload = {
      startDate: this.startDate,
      endDate: this.endDate,
      page: this.page,
      page_size: this.pageSize
    };

    this.loadService.filterLoads(payload).subscribe((res) => {
      this.isLoading = false;
      if (res.status === 'success' && res.loads) {
        this.loads = res.loads;
        this.totalPages = res.pages ?? 1;
      } else {
        this.loads = [];
        this.totalPages = 1;
      }
    }, (error) => {
      this.isLoading = false;
      this.messageDialogService.error('Failed to fetch loads');
    });
  }

  viewLoadReport(load: FilteredLoad): void {
    this.selectedLoad = load;
    this.isReportLoading = true;
    this.loadReportData = null;

    this.deliveryReportService.getLoadReport(load.id).subscribe((res) => {
      this.isReportLoading = false;
      if (res.status === 'success') {
        this.loadReportData = res;
      } else {
        this.messageDialogService.error(res.message || 'Failed to load report');
      }
    }, (error) => {
      this.isReportLoading = false;
      this.messageDialogService.error('Failed to load report');
    });
  }

  nextPage(): void {
    if (this.page < this.totalPages) {
      this.page++;
      this.fetchLoads();
    }
  }

  prevPage(): void {
    if (this.page > 1) {
      this.page--;
      this.fetchLoads();
    }
  }

  exportToCSV(): void {
    if (!this.loadReportData || !this.loadReportData.rows) {
      this.messageDialogService.warning('No data to export');
      return;
    }

    const csvContent = this.generateCSVContent(this.loadReportData.rows);
    this.downloadCSV(csvContent, `load-report-${this.selectedLoad?.name || 'report'}.csv`);
  }

  exportToExcel(): void {
    if (!this.loadReportData || !this.loadReportData.rows) {
      this.messageDialogService.warning('No data to export');
      return;
    }

    // For Excel export, we'll use CSV format as it's more universally supported
    // In a real application, you might want to use a library like xlsx
    this.exportToCSV();
  }

  private generateCSVContent(rows: LoadReportRow[]): string {
    const headers = [
      'SL No',
      'Customer Name',
      'Order Number',
      'Amount',
      'Invoice Number',
      'Invoice Amount',
      'Old Unpaid Invoices',
      'Old Unpaid Total',
      'Total',
      'Notes'
    ];

    const csvRows = [headers.join(',')];

    rows.forEach(row => {
      const csvRow = [
        row.slno,
        `"${row.customerName || ''}"`,
        `"${row.orderNumber || ''}"`,
        row.amount,
        `"${row.invoiceNumber || ''}"`,
        row.invoiceAmount,
        `"${row.oldUnpaidInvoiceNumbers.join(', ')}"`,
        row.oldUnpaidTotalAmount,
        row.total,
        `"${row.notes || ''}"`
      ];
      csvRows.push(csvRow.join(','));
    });

    // Add totals row if available
    if (this.loadReportData?.totals) {
      const totalsRow = [
        '',
        '',
        '',
        '',
        '',
        this.loadReportData.totals.invoiceAmount,
        '',
        this.loadReportData.totals.oldUnpaidTotalAmount,
        this.loadReportData.totals.total,
        ''
      ];
      csvRows.push(totalsRow.join(','));
    }

    return csvRows.join('\n');
  }

  private downloadCSV(content: string, filename: string): void {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  clearReport(): void {
    this.selectedLoad = null;
    this.loadReportData = null;
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  }

  formatOldUnpaidInvoices(invoices: string[]): string {
    return invoices.length > 0 ? invoices.join(', ') : '-';
  }
}
